/**
  * @class meleTimestamp
  * @description
  *
  * # Purpose
  * Implements `<mele-timestamp>` custom web element.
  *
  * # Use
  * Create element in HTML:
  * ```
  * parentEl.innerHTML += '<mele-timestamp></mele-timestamp>';
  * ```
  * Create element in JavaScript:
  * ```
  * parentEl.appendChildren({
  *   tag: 'mele-timestamp',
  *   ... other attributes ...
  * })
  * ```
  *
  * # Implementation notes
  *
  */

import meleView from 'meleView'
const moment = require('moment')
const TYPE = {
  datetimems: ['year', 'month', 'day', 'hour', 'minute', 'second'],
  datetime: ['year', 'month', 'day', 'hour', 'minute'],
  date: ['year', 'month', 'day'],
  time: ['hour', 'minute'],
}
const PROPERTIES = _.extend({}, {
  time: { type: 'number', description: 'unix time' },
  type: { type: 'string', description: 'type of datetime element', enum: ['datetime', 'date', 'time', 'datetimems'] },
  selected: { type: 'string', description: 'selected/focused input' },
  eventSelected: { type: 'function', description: 'called when time changes' },
  eventValuesChanged: { type: 'function', description: 'called when time changes' },
}, meleView._properties)

export default class meleTimestamp extends meleView {
  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Creates internal DOM structure.
  */
  init() {
    super.init()
    this.timeChanged()
    this._year = this._body.appendChildren({ tag: 'mele-sinput', label: 'calendar.${key}', key: 'year', readonly: true, class: 'dt year', placeholder: 'YYYY', validation: ['digits:4', 'date_format:YYYY'], eventFocusChanged: (el)=>this.selected = el.key })
    this._month = this._body.appendChildren({ tag: 'mele-sinput', label: 'calendar.${key}', key: 'month', readonly: true, class: 'dt month', placeholder: 'MM', validation: ['digits:2', 'date_format:MM'], eventFocusChanged: (el)=>this.selected = el.key })
    this._day = this._body.appendChildren({ tag: 'mele-sinput', label: 'calendar.${key}', key: 'day', readonly: true, class: 'dt day', placeholder: 'DD', validation: ['digits:2', 'date_format:DD'], eventFocusChanged: (el)=>this.selected = el.key })
    this._divider = this._body.appendChildren({ tag: 'span' })
    this._hour = this._body.appendChildren({ tag: 'mele-sinput', label: 'calendar.${key}', key: 'hour', readonly: true, class: 'dt hour', placeholder: 'HH', validation: ['digits:2', 'date_format:HH'], eventFocusChanged: (el)=>this.selected = el.key })
    this._minute = this._body.appendChildren({ tag: 'mele-sinput', label: 'calendar.${key}', key: 'minute', readonly: true, class: 'dt minute', placeholder: 'mm', validation: ['digits:2', 'date_format:mm'], eventFocusChanged: (el)=>this.selected = el.key })
    this._second = this._body.appendChildren({ tag: 'mele-sinput', label: 'calendar.${key}', key: 'second', readonly: true, class: 'dt second', placeholder: 'ss', validation: ['digits:2', 'date_format:ss'], eventFocusChanged: (el)=>this.selected = el.key })
  } // init

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "time"
  */
  timeChanged(value) {
    if(value) {
      this.renderInputs(value)
    }
  } // timeChanged

  typeChanged(value) {
    if(value) {
      const has_divider = /datetime|datetimems/.test(value)
      this._divider.hidden = !has_divider

      let allowed = TYPE[value]
      if(!allowed) allowed = []
      this._body.forAll('mele-sinput', el=>{
        el.hidden = !allowed.includes(el.key)
      })
    }
  }

  renderInputs(datetime) {
    const dt = moment(parseInt(datetime)).isValid() ? moment.unix(datetime) : moment()
    this._year.value = dt.year()
    this._month.value = _.two(dt.month()+1)
    this._day.value = _.two(dt.date())
    this._hour.value = _.two(dt.hour())
    this._minute.value = _.two(dt.minute())
    this._second.value = _.two(dt.second())
  }

  selectedChanged(value, old, context_user_interaction) {
    if(value) {
      if(value == old) return
      if(typeof this.eventSelected==='function') {
        this.eventSelected(this, value, old, context_user_interaction)
      }
      this.forAll('mele-sinput', el=>{
        el.classList.toggle('selected', el.key==value)
      })
    }
  }
} // class meleTimestamp

meleTimestamp._properties = PROPERTIES
meleTimestamp.observedAttributes = _.keys(PROPERTIES)

window.customElements.define('mele-timestamp', meleTimestamp)
